import React, { useState } from "react";
import { WORLDS, STATUS, STATUS_LABEL, NEEDS_YOU } from "../config/worlds";
import { useAgentLog } from "../hooks/useAgentEvents";

/**
 * Slide-in panel shell. mode: "king" (royal ledger) | "agent" (agent sheet).
 */
export default function FocusPanel({ db, mode, open, side = "right", agents, agent, api, onClose, onJumpWorld, onJumpAgent }) {
  const isKing = mode === "king";
  const world = agent ? WORLDS[agent.world] : null;
  const color = isKing ? "#F5B912" : world?.color ?? "#F5B912";

  return (
    <div
      className={`absolute bottom-4 z-30 flex w-[min(420px,92vw)] flex-col rounded-2xl border-2 border-white/20 bg-[#122A4A]/90 shadow-2xl backdrop-blur-md transition-all duration-700 [transition-timing-function:cubic-bezier(.32,.72,.24,1)] ${
        side === "left" ? "left-4 top-[122px]" : "right-4 top-[66px]"
      } ${open ? "translate-x-0 opacity-100" : side === "left" ? "pointer-events-none -translate-x-[110%] opacity-0" : "pointer-events-none translate-x-[110%] opacity-0"}`}
    >
      <div
        className="flex items-center gap-3 rounded-t-2xl border-b-[3px] border-white/25 px-4 py-3"
        style={{ background: `linear-gradient(160deg, ${color}, color-mix(in srgb, ${color} 60%, #06182E))` }}
      >
        <span className="grid h-10 w-10 flex-none place-items-center rounded-xl border-2 border-white/50 bg-white/20 text-xl">
          {isKing ? "👑" : world?.emblem}
        </span>
        <div className="min-w-0">
          <h2 className="truncate font-display text-xl font-semibold [text-shadow:0_2px_4px_rgba(0,0,0,.25)]">
            {isKing ? "The King" : agent?.name}
          </h2>
          <div className="text-[11px] font-bold uppercase tracking-widest text-white/85">
            {isKing
              ? "Royal ledger — all worlds"
              : `${agent?.district} · ${agent?.autonomous ? "Autonomous" : "Drafts"} · LV ${agent?.level}`}
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="ml-auto grid h-8 w-8 flex-none place-items-center rounded-lg bg-black/25 font-bold transition hover:scale-110 hover:bg-black/45">
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3.5 pb-4 pt-3 [scrollbar-width:thin]">
        {isKing ? (
          <KingLedger agents={agents} onJumpWorld={onJumpWorld} onJumpAgent={onJumpAgent} />
        ) : (
          agent && <AgentSheet key={agent.id} db={db} agent={agent} agents={agents} api={api} />
        )}
      </div>
    </div>
  );
}

/* ---------------- shared bits ---------------- */

const STATUS_COLOR = {
  [STATUS.WORKING]: "#3DDC7A",
  [STATUS.IDLE]: "#9DB6CF",
  [STATUS.APPROVE]: "#FFC53D",
  [STATUS.BLOCKED]: "#FF5A6E",
  [STATUS.OFFLINE]: "#5B7089",
};

function ago(ts) {
  const ms = ts?.toMillis?.();
  if (!ms) return "";
  const m = Math.round((Date.now() - ms) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 24 ? `${h} hr ago` : `${Math.round(h / 24)} d ago`;
}

function Stats({ set }) {
  const working = set.filter((a) => a.status === STATUS.WORKING).length;
  const need = set.filter((a) => NEEDS_YOU.includes(a.status)).length;
  const done = set.reduce((s, a) => s + a.doneToday, 0);
  const Cell = ({ v, k, cls }) => (
    <div className="flex-1 rounded-xl border border-white/10 bg-white/5 px-1 py-2 text-center">
      <div className={`font-display text-[19px] font-semibold tabular-nums ${cls ?? ""}`}>{v}</div>
      <div className="mt-px text-[9.5px] font-extrabold uppercase tracking-widest text-sky-300/60">{k}</div>
    </div>
  );
  return (
    <div className="mb-3 flex gap-2">
      <Cell v={set.length} k="Agents" />
      <Cell v={working} k="Working" cls="text-emerald-400" />
      <Cell v={need} k="Need you" cls={need ? "text-amber-400" : "text-emerald-400"} />
      <Cell v={done} k="Done today" />
    </div>
  );
}

function SectionHead({ children }) {
  return <div className="mx-0.5 mb-2 mt-3 text-[10.5px] font-extrabold uppercase tracking-[.14em] text-sky-300/60">{children}</div>;
}

/* ---------------- king ledger ---------------- */

function KingLedger({ agents, onJumpWorld, onJumpAgent }) {
  const needs = agents.filter((a) => NEEDS_YOU.includes(a.status));
  return (
    <>
      <Stats set={agents} />
      <SectionHead>Quests waiting on the crown</SectionHead>
      {needs.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-white/25 p-5 text-center text-[13px] font-bold text-sky-200/80">
          <span className="mb-1 block font-display text-base text-emerald-400">✦ ALL CLEAR ✦</span>
          The kingdom runs itself. Nothing needs your signature.
        </div>
      ) : (
        needs.map((a) => {
          const w = WORLDS[a.world];
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => onJumpAgent(a.world, a.id)}
              className={`mb-2 flex w-full items-center gap-2.5 rounded-xl border border-white/10 border-l-4 bg-white/5 p-2.5 text-left transition hover:translate-x-0.5 hover:bg-white/10 ${
                a.status === STATUS.BLOCKED ? "border-l-rose-400" : "border-l-amber-400"
              }`}
            >
              <span className="flex-none text-lg">{a.status === STATUS.BLOCKED ? "⚠️" : "📜"}</span>
              <span className="min-w-0 flex-1">
                <span className="block font-display text-[13.5px] font-semibold">{a.name}</span>
                <span className="block truncate text-[11.5px] font-semibold text-sky-200/85">{a.task}</span>
              </span>
              <span className="flex-none rounded-md px-2 py-0.5 text-[9.5px] font-extrabold uppercase tracking-wider" style={{ background: `${w.color}33`, color: w.color, border: `1px solid ${w.color}66` }}>
                {a.world}
              </span>
            </button>
          );
        })
      )}

      <SectionHead>The three worlds</SectionHead>
      {Object.keys(WORLDS).map((k) => {
        const w = WORLDS[k];
        const set = agents.filter((a) => a.world === k);
        const need = set.filter((a) => NEEDS_YOU.includes(a.status)).length;
        return (
          <button
            key={k}
            type="button"
            onClick={() => onJumpWorld(k)}
            className="mb-2 flex w-full items-center gap-2.5 rounded-xl border border-white/10 border-l-4 bg-white/5 p-2.5 text-left transition hover:translate-x-0.5 hover:bg-white/10"
            style={{ borderLeftColor: w.color }}
          >
            <span className="flex-none text-lg">{w.emblem}</span>
            <span className="min-w-0 flex-1">
              <span className="block font-display text-[13.5px] font-semibold">{w.name}</span>
              <span className="block text-[11.5px] font-semibold text-sky-200/85">
                {set.filter((a) => a.status === STATUS.WORKING).length} working · {set.reduce((s, a) => s + a.doneToday, 0)} done today
              </span>
            </span>
            <span className={`flex-none rounded-md px-2 py-0.5 text-[9.5px] font-extrabold uppercase tracking-wider ${need ? "bg-amber-400/20 text-amber-300" : "bg-emerald-400/15 text-emerald-400"}`}>
              {need ? `${need} need you` : "clear"}
            </span>
          </button>
        );
      })}
    </>
  );
}

/* ---------------- agent sheet ---------------- */


const EVENT_ICON = { completed: "✅", handoff: "📨", error: "⚠️", approval_requested: "📜", command: "👑", note: "•" };

function AgentSheet({ db, agent, agents, api }) {
  const [showLog, setShowLog] = useState(false);
  const col = STATUS_COLOR[agent.status];
  const offline = agent.status === STATUS.OFFLINE;
  const neighbors = agents.filter((x) => x.world === agent.world && x.district === agent.district && x.id !== agent.id);
  const upNext = (agent.upNext ?? []).filter((t) => t !== agent.task);

  return (
    <>
      <div className="mb-3 rounded-xl border border-white/10 bg-black/30 p-3">
        <div className="flex items-center gap-2 text-[10px] font-extrabold uppercase tracking-[.14em] text-sky-300/60">
          <span className="h-2 w-2 rounded-full" style={{ background: col }} />
          {STATUS_LABEL[agent.status]}
          {agent.updatedAt && <span className="ml-auto normal-case tracking-normal text-sky-300/50">{ago(agent.updatedAt)}</span>}
        </div>
        <div className="mt-1.5 text-[13.5px] font-bold leading-snug">
          {offline ? (
            <>No report from this agent yet. Once Grok Bot calls <code className="rounded bg-white/10 px-1 text-[12px]">report_agent_status</code> for <code className="rounded bg-white/10 px-1 text-[12px]">{agent.id}</code>, its work shows up here live.</>
          ) : agent.task ? (
            agent.task
          ) : (
            "Resting at camp — queue empty."
          )}
        </div>
        {agent.message && (
          <div className={`mt-1.5 text-[12px] font-semibold leading-snug ${agent.status === STATUS.BLOCKED ? "text-rose-300" : "text-sky-200/80"}`}>
            {agent.message}
          </div>
        )}
        {agent.link && (
          <a href={agent.link} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 rounded-lg border border-white/20 bg-white/10 px-2.5 py-1 font-display text-[12px] font-semibold text-amber-300 transition hover:bg-white/20">
            Open the work ↗
          </a>
        )}
        {!offline && (
          <>
            <div className="mt-2.5 h-2 overflow-hidden rounded border border-white/10 bg-black/35">
              <div className="h-full rounded transition-[width] duration-500" style={{ width: `${Math.round(agent.progress)}%`, background: col }} />
            </div>
            <div className="mt-1 flex text-[10.5px] font-extrabold text-sky-300/70">
              <span>{Math.round(agent.progress)}% complete</span>
              <span className="ml-auto">{agent.doneToday} done today</span>
            </div>
          </>
        )}
      </div>

      <div className="mb-3 flex gap-2">
        {agent.status === STATUS.APPROVE && (
          <button type="button" onClick={() => api.approve(agent.id)} className="flex-1 rounded-xl bg-amber-400 py-2 font-display text-[12.5px] font-semibold text-amber-950 shadow transition hover:scale-[1.03] hover:brightness-110">
            ✓ Approve &amp; release
          </button>
        )}
        {agent.status === STATUS.BLOCKED && (
          <button type="button" onClick={() => api.retry(agent.id)} className="flex-1 rounded-xl bg-rose-500 py-2 font-display text-[12.5px] font-semibold text-white shadow transition hover:scale-[1.03] hover:brightness-110">
            ↻ Clear &amp; retry
          </button>
        )}
        {(agent.status === STATUS.WORKING || agent.status === STATUS.IDLE) && (
          <button
            type="button"
            onClick={() => (agent.status === STATUS.IDLE ? api.resume(agent.id) : api.pause(agent.id))}
            className="flex-1 rounded-xl border-[1.5px] border-white/20 py-2 font-display text-[12.5px] font-semibold text-sky-200 transition hover:scale-[1.03] hover:text-white"
          >
            {agent.status === STATUS.IDLE ? "▶ Wake agent" : "‖ Pause agent"}
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowLog((v) => !v)}
          className={`flex-1 rounded-xl border-[1.5px] py-2 font-display text-[12.5px] font-semibold transition hover:scale-[1.03] hover:text-white ${showLog ? "border-amber-400/70 text-white" : "border-white/20 text-sky-200"}`}
        >
          {showLog ? "Hide log" : "Open log"}
        </button>
      </div>

      {showLog ? (
        <AgentLog db={db} agentId={agent.id} />
      ) : (
        <>
          <SectionHead>Up next</SectionHead>
          {upNext.length === 0 ? (
            <div className="text-[12.5px] font-semibold text-sky-300/60">Nothing queued.</div>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {upNext.map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-[12.5px] font-semibold leading-snug text-sky-200/90">
                  <span className="mt-px flex-none rounded-md border-[1.5px] border-white/15 px-1.5 font-display text-[10px] text-sky-300/60">{i + 1}</span>
                  {t}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {neighbors.length > 0 && (
        <>
          <SectionHead>District</SectionHead>
          <ul className="flex flex-col gap-1.5">
            {neighbors.map((x) => (
              <li key={x.id} className="flex items-start gap-2 text-[12.5px] font-semibold text-sky-200/80">
                <span className="mt-1 h-2 w-2 flex-none rounded-full" style={{ background: STATUS_COLOR[x.status] }} />
                {x.name} — {STATUS_LABEL[x.status].toLowerCase()}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

function AgentLog({ db, agentId }) {
  const { events, loading } = useAgentLog(db, agentId);
  return (
    <>
      <SectionHead>Activity log</SectionHead>
      {loading ? (
        <div className="text-[12.5px] font-semibold text-sky-300/60">Loading…</div>
      ) : events.length === 0 ? (
        <div className="text-[12.5px] font-semibold text-sky-300/60">No activity logged yet.</div>
      ) : (
        <ul className="flex flex-col gap-2">
          {events.map((e) => (
            <li key={e.id} className="flex items-start gap-2 text-[12.5px] font-semibold leading-snug text-sky-200/90">
              <span className="w-4 flex-none text-center">{EVENT_ICON[e.type] ?? "•"}</span>
              <span className="min-w-0 flex-1">
                {e.message}
                {e.type === "handoff" && e.toAgentId && <span className="text-sky-300/60"> → {e.toAgentId}</span>}
              </span>
              <span className="flex-none text-[10.5px] font-bold text-sky-300/50">{ago(e.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
